import React from 'react';

// See https://hello.atlassian.net/wiki/spaces/EDAI/pages/4834594169/LDR+Keeping+Emotion+Global+usages
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, Global } from '@emotion/react';

import { token } from '@atlaskit/tokens';

/**
 * Per-node-type hover highlight styles for the suggestion icon hover, mirroring
 * how the block controls "remix" selection treats container, extension and rule
 * nodes. Default text blocks and cards keep the inline box-shadow highlight set
 * directly on their decoration, so they aren't styled here.
 *
 * The class selectors below are inlined as string literals (rather than imported
 * from `hoverHighlightClassNames`) because the ui-styling-standard rules disallow
 * dynamic/imported object-style keys. Keep them in sync with
 * `hoverHighlightClassNames` in `../../pm-plugins/hoverHighlightDecorations`.
 */
const hoverHighlightStyles = css({
	// Container nodes (panel, codeBlock, expand, layoutSection): a translucent blue
	// blanket overlay on top of the content + a subtle ring, instead of a
	// box-shadow that would look wrong on full-bleed nodes.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.ProseMirror .ai-suggestions-hover-overlay': {
		position: 'relative',
		boxShadow: `0 0 0 ${token('border.width')} ${token('color.border.focused')}`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.ProseMirror .ai-suggestions-hover-overlay::before': {
		content: '""',
		position: 'absolute',
		inset: 0,
		zIndex: 1,
		pointerEvents: 'none',
		backgroundColor: token('color.blanket.selected'),
	},
	// Tables: draw a uniform 1px blue border + translucent blue background on every
	// cell via the cell's ::after overlay, mirroring how "remix" highlights a
	// selected table. The -1px offsets (matching the editor's own selected-cell
	// overlay) make the border sit on top of the collapsed shared borders so both
	// horizontal and vertical lines render, and header cells get the same uniform
	// border.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.ProseMirror .ai-suggestions-hover-table td, .ProseMirror .ai-suggestions-hover-table th': {
		position: 'relative',
	},
	// !important overrides the table plugin's own base cell ::after, which has
	// higher selector specificity and uses width/height: 100% with no offsets.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.ProseMirror .ai-suggestions-hover-table td::after, .ProseMirror .ai-suggestions-hover-table th::after':
		{
			content: '""',
			position: 'absolute',
			pointerEvents: 'none',
			zIndex: 2,
			/* eslint-disable @atlaskit/ui-styling-standard/no-important-styles */
			top: '-1px !important',
			right: '-1px !important',
			bottom: '-1px !important',
			left: '-1px !important',
			width: 'auto !important',
			height: 'auto !important',
			border: `${token('border.width')} solid ${token('color.border.focused')} !important`,
			backgroundColor: `${token('color.blanket.selected')} !important`,
			/* eslint-enable @atlaskit/ui-styling-standard/no-important-styles */
		},
	// Extensions: highlight the inner container rather than the wrapper.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.ProseMirror .ai-suggestions-hover-extension .extension-container': {
		boxShadow: `0 0 0 ${token('border.width')} ${token('color.border.focused')}`,
	},
	// Rule: fill the line so the thin node is visibly highlighted.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.ProseMirror .ai-suggestions-hover-rule': {
		backgroundColor: token('color.border.focused'),
	},
});

export const HoverHighlightGlobalStyles = (): JSX.Element => (
	<Global styles={hoverHighlightStyles} />
);
